/**
 * 地理距離計算ユーティリティ
 * Mapboxのturf依存を避け、Haversine式を直接実装。
 */
import type { LngLat } from '@/types/property';

const EARTH_RADIUS_METERS = 6_371_008.8;

function toRadians(deg: number): number {
  return (deg * Math.PI) / 180;
}

/**
 * 2地点の直線距離(メートル)をHaversine式で計算。
 */
export function haversineMeters(a: LngLat, b: LngLat): number {
  const dLat = toRadians(b.lat - a.lat);
  const dLng = toRadians(b.lng - a.lng);
  const lat1 = toRadians(a.lat);
  const lat2 = toRadians(b.lat);

  const sinDLat = Math.sin(dLat / 2);
  const sinDLng = Math.sin(dLng / 2);
  const h =
    sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;
  const c = 2 * Math.asin(Math.min(1, Math.sqrt(h)));
  return EARTH_RADIUS_METERS * c;
}

/**
 * メートルを表示用に丸める。
 * - 1000m未満: 10mごとに丸めて "550m"
 * - 1000m以上: 100mごとに "1.2km"
 */
export function formatDistance(meters: number): string {
  if (meters < 1000) {
    return `${Math.round(meters / 10) * 10}m`;
  }
  const km = Math.round(meters / 100) / 10;
  return `${km}km`;
}

/**
 * LineString座標列の「累積距離の中点」を返す。
 * 単純な配列の中央indexではなく、実際の道のりの中央地点に近い座標を計算する。
 * (頂点不均一な経路でも、見た目で経路の真ん中にラベルが置けるようにするため)
 */
export function lineMidpointByDistance(
  coords: Array<[number, number]>,
): { lng: number; lat: number } | null {
  if (coords.length < 2) return null;
  if (coords.length === 2) {
    const a = coords[0]!;
    const b = coords[1]!;
    return { lng: (a[0] + b[0]) / 2, lat: (a[1] + b[1]) / 2 };
  }

  // 1. 累積距離テーブル
  const cumulative: number[] = [0];
  for (let i = 1; i < coords.length; i++) {
    const a = coords[i - 1]!;
    const b = coords[i]!;
    const d = haversineMeters(
      { lng: a[0], lat: a[1] },
      { lng: b[0], lat: b[1] },
    );
    cumulative.push(cumulative[i - 1]! + d);
  }
  const total = cumulative[cumulative.length - 1]!;
  const half = total / 2;

  // 2. half以上の累積距離を持つ最初のindexを探し、その手前の頂点との間で線形補間
  for (let i = 1; i < cumulative.length; i++) {
    if (cumulative[i]! >= half) {
      const segLen = cumulative[i]! - cumulative[i - 1]!;
      const remaining = half - cumulative[i - 1]!;
      const t = segLen === 0 ? 0 : remaining / segLen;
      const a = coords[i - 1]!;
      const b = coords[i]!;
      return {
        lng: a[0] + (b[0] - a[0]) * t,
        lat: a[1] + (b[1] - a[1]) * t,
      };
    }
  }
  const last = coords[coords.length - 1]!;
  return { lng: last[0], lat: last[1] };
}
